import React, { useCallback, useMemo } from 'react'
import { Autocomplete, Loader } from '@manychat/manyui'

import { useFilterFieldValues } from 'common/filter/hooks'

import { DefaultLayout } from './components/DefaultLayout'
import { SearchInput } from './components/SearchInput'
import { WarningLayoutFBPolicy } from './components/WarningLayoutFBPolicy'
import {
  FieldValuePickerDefaultOption,
  FieldValuePickerProps,
  HandleValueChange,
  InputRefHandler,
  RenderOption,
} from './interfaces'
import { FOLLOW_UP_FIELD, OUTSIDE_24_WINDOW_FIELD, generateFieldValuePickerConfig } from './utils'

import cm from './FieldValuePicker.module.css'

export const FieldValuePicker = ({
  condition,
  label,
  onChange,
  fieldId,
  value,
}: FieldValuePickerProps) => {
  const fieldValues = useFilterFieldValues({
    fieldId,
    query: value,
    type: 'segment',
  })
  const { options, showSearch } = useMemo(
    () => generateFieldValuePickerConfig(fieldValues.items),
    [fieldValues.items],
  )

  const handleValueChange: HandleValueChange = useCallback(
    (event) => {
      const modifiedEvent = {
        ...event,
        target: { ...event.target, value: event.target.option.value },
      }

      onChange(modifiedEvent, true)
    },
    [onChange],
  )

  const handleInputRef: InputRefHandler = useCallback((element) => {
    element?.select()
  }, [])

  const getRenderOptions: RenderOption = useCallback<RenderOption>((mixedOption, label) => {
    const option = mixedOption as FieldValuePickerDefaultOption
    switch (option.value) {
      case FOLLOW_UP_FIELD:
      case OUTSIDE_24_WINDOW_FIELD:
        return <WarningLayoutFBPolicy label={label} count={option.count} />
      default:
        return <DefaultLayout label={label} count={option.count} />
    }
  }, [])

  if (fieldValues.isLoading) {
    return (
      <div className={cm.wrapper}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={cm.wrapper}>
      <Autocomplete
        ref={handleInputRef}
        options={options as FieldValuePickerDefaultOption[]}
        value={condition.value}
        onChange={handleValueChange}
        noPortal
        noClearOnEmpty
        allowNew={false}
        customInput={<SearchInput label={label} hidden={!showSearch} />}
        renderOption={getRenderOptions}
        label={label}
      />
    </div>
  )
}
